'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import AppNav from '@/components/AppNav';
import { PageErrorBoundary } from '@/components/ErrorBoundary';
import { useToast } from '@/components/Toast';

// ─── Types ───────────────────────────────────────────────────────────────────
interface FormData {
  // Step 1 – Business Passport
  businessType: string;
  proposedName1: string;
  proposedName2: string;
  proposedName3: string;
  // Step 2 – Directors
  directors: Director[];
  // Step 3 – Shareholders
  shareholders: Shareholder[];
  // Step 4 – Registered Office
  regOfficeAddress: string;
  regOfficeCity: string;
  regOfficeState: string;
  regOfficePincode: string;
  // Step 5 – Capital
  authorisedCapital: string;
  paidUpCapital: string;
  // Step 6 – Objects
  mainObjects: string;
  otherObjects: string;
  // Step 7 – MOA
  moaClause1: string;
  moaClause2: string;
  // Step 8 – AOA
  aoaType: string;
  // Step 9 – INC-9
  inc9Declarations: boolean[];
  // Step 10 – Documents
  documentsUploaded: string[];
  // Step 11 – Review
  reviewConfirmed: boolean;
  // Step 12 – Submit
  paymentDone: boolean;
}

interface Director {
  name: string;
  din: string;
  pan: string;
  email: string;
  phone: string;
  designation: string;
  isSubscriber: boolean;
}

interface Shareholder {
  name: string;
  shares: string;
  amount: string;
  type: string;
}

const STEPS = [
  { id: 1, label: 'Business Passport', icon: '🧭', desc: 'AI-filled from your profile' },
  { id: 2, label: 'Directors', icon: '👤', desc: 'DIN, PAN, KYC details' },
  { id: 3, label: 'Shareholders', icon: '📊', desc: 'Share allocation' },
  { id: 4, label: 'Registered Office', icon: '🏢', desc: 'Address & NOC' },
  { id: 5, label: 'Share Capital', icon: '💰', desc: 'Authorised & paid-up' },
  { id: 6, label: 'Objects Clause', icon: '📋', desc: 'Business activities (AI-drafted)' },
  { id: 7, label: 'eMOA (INC-33)', icon: '📜', desc: 'Memorandum of Association' },
  { id: 8, label: 'eAOA (INC-34)', icon: '📑', desc: 'Articles of Association' },
  { id: 9, label: 'INC-9 Declaration', icon: '✍️', desc: 'Subscriber declarations' },
  { id: 10, label: 'Documents', icon: '📁', desc: 'Upload & verify' },
  { id: 11, label: 'Pre-Filing Review', icon: '🔍', desc: 'AI confidence check' },
  { id: 12, label: 'Submit to MCA', icon: '🚀', desc: 'SPICe+ e-filing' },
];

const INDIAN_STATES = [
  'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh',
  'Goa', 'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jharkhand', 'Karnataka',
  'Kerala', 'Madhya Pradesh', 'Maharashtra', 'Manipur', 'Meghalaya', 'Mizoram',
  'Nagaland', 'Odisha', 'Punjab', 'Rajasthan', 'Sikkim', 'Tamil Nadu',
  'Telangana', 'Tripura', 'Uttar Pradesh', 'Uttarakhand', 'West Bengal',
  'Delhi', 'Chandigarh', 'Puducherry',
];

const emptyDirector = (): Director => ({
  name: '', din: '', pan: '', email: '', phone: '', designation: 'Director', isSubscriber: true,
});

const emptyShareholder = (): Shareholder => ({
  name: '', shares: '', amount: '', type: 'Equity',
});

const defaultFormData: FormData = {
  businessType: 'Private Limited Company',
  proposedName1: 'Elysium Fragrances Private Limited',
  proposedName2: 'Elysium Scents Private Limited',
  proposedName3: 'Elysium Perfumes Private Limited',
  directors: [
    { name: 'Arjun Mehta', din: '', pan: 'ABCPM1234D', email: 'arjun@elysium.in', phone: '9876543210', designation: 'Managing Director', isSubscriber: true },
    { name: 'Priya Sharma', din: '', pan: 'XYZPS5678E', email: 'priya@elysium.in', phone: '9876543211', designation: 'Director', isSubscriber: true },
  ],
  shareholders: [
    { name: 'Arjun Mehta', shares: '5000', amount: '50000', type: 'Equity' },
    { name: 'Priya Sharma', shares: '5000', amount: '50000', type: 'Equity' },
  ],
  regOfficeAddress: '42, Perfume Lane, Bandra West',
  regOfficeCity: 'Mumbai',
  regOfficeState: 'Maharashtra',
  regOfficePincode: '400050',
  authorisedCapital: '1000000',
  paidUpCapital: '100000',
  mainObjects: 'To manufacture, produce, blend, compound, prepare, import, export, buy, sell, and deal in perfumes, fragrances, cosmetics, essential oils, and related products.',
  otherObjects: 'To carry on the business of retail and wholesale trading of luxury goods, beauty products, and personal care items.',
  moaClause1: 'The name of the Company is Elysium Fragrances Private Limited.',
  moaClause2: 'The Registered Office of the Company will be situated in the State of Maharashtra.',
  aoaType: 'Table F (Standard)',
  inc9Declarations: [true, true, true, true, true],
  documentsUploaded: ['PAN Card - Arjun Mehta', 'Aadhaar - Arjun Mehta', 'PAN Card - Priya Sharma'],
  reviewConfirmed: false,
  paymentDone: false,
};

// ─── Step Components ──────────────────────────────────────────────────────────
function StepBusinessPassport({ data, onChange }: { data: FormData; onChange: (k: keyof FormData, v: any) => void }) {
  return (
    <div className="space-y-5">
      <div className="bg-ai-light border border-ai/20 rounded-xl p-4 flex items-start gap-3">
        <span className="text-ai text-lg">✨</span>
        <div>
          <p className="text-[13px] font-semibold text-ai">AI Pre-filled from Business Passport</p>
          <p className="text-[12px] text-ink-muted mt-0.5">Your profile data has been used to auto-fill this form. Review and edit as needed.</p>
        </div>
      </div>
      <div>
        <label className="block text-[12px] font-semibold text-ink-muted uppercase tracking-wide mb-2">Business Type</label>
        <select
          value={data.businessType}
          onChange={e => onChange('businessType', e.target.value)}
          className="w-full bg-surface-2 border border-border rounded-xl px-4 py-3 text-[14px] text-ink focus:outline-none focus:ring-2 focus:ring-ai/20 focus:border-ai/40"
        >
          {['Private Limited Company', 'One Person Company (OPC)', 'Limited Liability Partnership (LLP)', 'Public Limited Company'].map(t => (
            <option key={t}>{t}</option>
          ))}
        </select>
      </div>
      <div>
        <label className="block text-[12px] font-semibold text-ink-muted uppercase tracking-wide mb-2">Proposed Company Name (1st Choice)</label>
        <input
          value={data.proposedName1}
          onChange={e => onChange('proposedName1', e.target.value)}
          className="w-full bg-surface-2 border border-border rounded-xl px-4 py-3 text-[14px] text-ink focus:outline-none focus:ring-2 focus:ring-ai/20 focus:border-ai/40"
        />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-[12px] font-semibold text-ink-muted uppercase tracking-wide mb-2">2nd Choice</label>
          <input value={data.proposedName2} onChange={e => onChange('proposedName2', e.target.value)} className="w-full bg-surface-2 border border-border rounded-xl px-4 py-3 text-[14px] text-ink focus:outline-none focus:ring-2 focus:ring-ai/20 focus:border-ai/40" />
        </div>
        <div>
          <label className="block text-[12px] font-semibold text-ink-muted uppercase tracking-wide mb-2">3rd Choice</label>
          <input value={data.proposedName3} onChange={e => onChange('proposedName3', e.target.value)} className="w-full bg-surface-2 border border-border rounded-xl px-4 py-3 text-[14px] text-ink focus:outline-none focus:ring-2 focus:ring-ai/20 focus:border-ai/40" />
        </div>
      </div>
      <div className="bg-surface-2 rounded-xl p-4">
        <p className="text-[11px] font-semibold text-ink-muted uppercase tracking-wide mb-2">Name Availability Check</p>
        <div className="flex items-center gap-2">
          <span className="w-2 h-2 bg-warning rounded-full"></span>
          <p className="text-[12px] text-ink-muted">Name availability will be checked with MCA RUN (Reserve Unique Name) service before filing.</p>
        </div>
      </div>
    </div>
  );
}

function StepDirectors({ data, onChange }: { data: FormData; onChange: (k: keyof FormData, v: any) => void }) {
  const addDirector = () => onChange('directors', [...data.directors, emptyDirector()]);
  const updateDirector = (i: number, field: keyof Director, val: any) => {
    const updated = data.directors.map((d, idx) => idx === i ? { ...d, [field]: val } : d);
    onChange('directors', updated);
  };
  const removeDirector = (i: number) => onChange('directors', data.directors.filter((_, idx) => idx !== i));

  return (
    <div className="space-y-5">
      {data.directors.map((dir, i) => (
        <div key={i} className="bg-surface-2 rounded-xl border border-border p-5">
          <div className="flex items-center justify-between mb-4">
            <p className="text-[13px] font-semibold text-ink">Director {i + 1}</p>
            {data.directors.length > 1 && (
              <button onClick={() => removeDirector(i)} className="text-[11px] text-danger hover:text-danger/70 transition-colors">Remove</button>
            )}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              { label: 'Full Name', field: 'name' as keyof Director },
              { label: 'DIN (if existing)', field: 'din' as keyof Director },
              { label: 'PAN Number', field: 'pan' as keyof Director },
              { label: 'Email', field: 'email' as keyof Director },
              { label: 'Phone', field: 'phone' as keyof Director },
            ].map(({ label, field }) => (
              <div key={field}>
                <label className="block text-[11px] font-semibold text-ink-muted uppercase tracking-wide mb-1.5">{label}</label>
                <input
                  value={dir[field] as string}
                  onChange={e => updateDirector(i, field, e.target.value)}
                  className="w-full bg-white border border-border rounded-lg px-3 py-2.5 text-[13px] text-ink focus:outline-none focus:ring-2 focus:ring-ai/20 focus:border-ai/40"
                />
              </div>
            ))}
            <div>
              <label className="block text-[11px] font-semibold text-ink-muted uppercase tracking-wide mb-1.5">Designation</label>
              <select
                value={dir.designation}
                onChange={e => updateDirector(i, 'designation', e.target.value)}
                className="w-full bg-white border border-border rounded-lg px-3 py-2.5 text-[13px] text-ink focus:outline-none focus:ring-2 focus:ring-ai/20 focus:border-ai/40"
              >
                {['Managing Director', 'Director', 'Whole-time Director', 'Independent Director'].map(d => <option key={d}>{d}</option>)}
              </select>
            </div>
          </div>
          <label className="flex items-center gap-2 mt-3 cursor-pointer">
            <input type="checkbox" checked={dir.isSubscriber} onChange={e => updateDirector(i, 'isSubscriber', e.target.checked)} className="rounded" />
            <span className="text-[12px] text-ink-muted">Also a subscriber to MOA/AOA</span>
          </label>
        </div>
      ))}
      {data.directors.length < 15 && (
        <button onClick={addDirector} className="w-full py-3 border-2 border-dashed border-border rounded-xl text-[13px] font-medium text-ink-muted hover:border-ai/30 hover:text-ai transition-all">
          + Add Director
        </button>
      )}
    </div>
  );
}

function StepShareholders({ data, onChange }: { data: FormData; onChange: (k: keyof FormData, v: any) => void }) {
  const addShareholder = () => onChange('shareholders', [...data.shareholders, emptyShareholder()]);
  const updateShareholder = (i: number, field: keyof Shareholder, val: string) => {
    const updated = data.shareholders.map((s, idx) => idx === i ? { ...s, [field]: val } : s);
    onChange('shareholders', updated);
  };
  const removeShareholder = (i: number) => onChange('shareholders', data.shareholders.filter((_, idx) => idx !== i));
  const totalShares = data.shareholders.reduce((sum, s) => sum + (parseInt(s.shares) || 0), 0);

  return (
    <div className="space-y-5">
      <div className="bg-surface-2 rounded-xl p-4 flex items-center justify-between">
        <div>
          <p className="text-[12px] font-semibold text-ink-muted">Total Shares Allocated</p>
          <p className="text-xl font-bold text-ink">{totalShares.toLocaleString()}</p>
        </div>
        <div>
          <p className="text-[12px] font-semibold text-ink-muted">Paid-up Capital</p>
          <p className="text-xl font-bold text-ink">₹{parseInt(data.paidUpCapital || '0').toLocaleString()}</p>
        </div>
      </div>
      {data.shareholders.map((sh, i) => (
        <div key={i} className="bg-surface-2 rounded-xl border border-border p-5">
          <div className="flex items-center justify-between mb-4">
            <p className="text-[13px] font-semibold text-ink">Shareholder {i + 1}</p>
            {data.shareholders.length > 1 && (
              <button onClick={() => removeShareholder(i)} className="text-[11px] text-danger hover:text-danger/70 transition-colors">Remove</button>
            )}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              { label: 'Full Name / Entity', field: 'name' as keyof Shareholder },
              { label: 'No. of Shares', field: 'shares' as keyof Shareholder },
              { label: 'Amount (₹)', field: 'amount' as keyof Shareholder },
            ].map(({ label, field }) => (
              <div key={field}>
                <label className="block text-[11px] font-semibold text-ink-muted uppercase tracking-wide mb-1.5">{label}</label>
                <input
                  value={sh[field]}
                  onChange={e => updateShareholder(i, field, e.target.value)}
                  className="w-full bg-white border border-border rounded-lg px-3 py-2.5 text-[13px] text-ink focus:outline-none focus:ring-2 focus:ring-ai/20 focus:border-ai/40"
                />
              </div>
            ))}
            <div>
              <label className="block text-[11px] font-semibold text-ink-muted uppercase tracking-wide mb-1.5">Share Type</label>
              <select
                value={sh.type}
                onChange={e => updateShareholder(i, 'type', e.target.value)}
                className="w-full bg-white border border-border rounded-lg px-3 py-2.5 text-[13px] text-ink focus:outline-none focus:ring-2 focus:ring-ai/20 focus:border-ai/40"
              >
                {['Equity', 'Preference', 'Compulsorily Convertible Preference Shares (CCPS)'].map(t => <option key={t}>{t}</option>)}
              </select>
            </div>
          </div>
        </div>
      ))}
      <button onClick={addShareholder} className="w-full py-3 border-2 border-dashed border-border rounded-xl text-[13px] font-medium text-ink-muted hover:border-ai/30 hover:text-ai transition-all">
        + Add Shareholder
      </button>
    </div>
  );
}

function StepRegisteredOffice({ data, onChange }: { data: FormData; onChange: (k: keyof FormData, v: any) => void }) {
  return (
    <div className="space-y-5">
      <div>
        <label className="block text-[12px] font-semibold text-ink-muted uppercase tracking-wide mb-2">Street Address</label>
        <input value={data.regOfficeAddress} onChange={e => onChange('regOfficeAddress', e.target.value)} className="w-full bg-surface-2 border border-border rounded-xl px-4 py-3 text-[14px] text-ink focus:outline-none focus:ring-2 focus:ring-ai/20 focus:border-ai/40" />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <label className="block text-[12px] font-semibold text-ink-muted uppercase tracking-wide mb-2">City</label>
          <input value={data.regOfficeCity} onChange={e => onChange('regOfficeCity', e.target.value)} className="w-full bg-surface-2 border border-border rounded-xl px-4 py-3 text-[14px] text-ink focus:outline-none focus:ring-2 focus:ring-ai/20 focus:border-ai/40" />
        </div>
        <div>
          <label className="block text-[12px] font-semibold text-ink-muted uppercase tracking-wide mb-2">State</label>
          <select value={data.regOfficeState} onChange={e => onChange('regOfficeState', e.target.value)} className="w-full bg-surface-2 border border-border rounded-xl px-4 py-3 text-[14px] text-ink focus:outline-none focus:ring-2 focus:ring-ai/20 focus:border-ai/40">
            {INDIAN_STATES.map(s => <option key={s}>{s}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-[12px] font-semibold text-ink-muted uppercase tracking-wide mb-2">Pincode</label>
          <input value={data.regOfficePincode} onChange={e => onChange('regOfficePincode', e.target.value)} maxLength={6} className="w-full bg-surface-2 border border-border rounded-xl px-4 py-3 text-[14px] text-ink focus:outline-none focus:ring-2 focus:ring-ai/20 focus:border-ai/40" />
        </div>
      </div>
      <div className="bg-warning/8 border border-warning/20 rounded-xl p-4">
        <p className="text-[12px] font-semibold text-warning mb-1">NOC Required</p>
        <p className="text-[12px] text-ink-muted">A No Objection Certificate from the property owner is required for the registered office address. Upload in Step 10.</p>
      </div>
    </div>
  );
}

function StepCapital({ data, onChange }: { data: FormData; onChange: (k: keyof FormData, v: any) => void }) {
  const auth = parseInt(data.authorisedCapital || '0');
  const paid = parseInt(data.paidUpCapital || '0');
  const govtFee = auth <= 100000 ? 5000 : auth <= 500000 ? 10000 : auth <= 1000000 ? 15000 : 30000;

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div>
          <label className="block text-[12px] font-semibold text-ink-muted uppercase tracking-wide mb-2">Authorised Capital (₹)</label>
          <input type="number" value={data.authorisedCapital} onChange={e => onChange('authorisedCapital', e.target.value)} className="w-full bg-surface-2 border border-border rounded-xl px-4 py-3 text-[14px] text-ink focus:outline-none focus:ring-2 focus:ring-ai/20 focus:border-ai/40" />
          <p className="text-[11px] text-ink-muted mt-1.5">Maximum capital the company can raise</p>
        </div>
        <div>
          <label className="block text-[12px] font-semibold text-ink-muted uppercase tracking-wide mb-2">Paid-up Capital (₹)</label>
          <input type="number" value={data.paidUpCapital} onChange={e => onChange('paidUpCapital', e.target.value)} className="w-full bg-surface-2 border border-border rounded-xl px-4 py-3 text-[14px] text-ink focus:outline-none focus:ring-2 focus:ring-ai/20 focus:border-ai/40" />
          <p className="text-[11px] text-ink-muted mt-1.5">Minimum ₹1 lakh for Pvt Ltd</p>
        </div>
      </div>
      {paid > auth && (
        <div className="bg-danger-light border border-danger/20 rounded-xl p-3">
          <p className="text-[12px] font-semibold text-danger">⚠ Paid-up capital cannot exceed authorised capital</p>
        </div>
      )}
      <div className="bg-surface-2 rounded-xl p-5">
        <p className="text-[12px] font-semibold text-ink-muted uppercase tracking-wide mb-3">Estimated Government Fees</p>
        <div className="space-y-2">
          <div className="flex justify-between text-[13px]">
            <span className="text-ink-muted">MCA Filing Fee (based on authorised capital)</span>
            <span className="font-semibold text-ink">₹{govtFee.toLocaleString()}</span>
          </div>
          <div className="flex justify-between text-[13px]">
            <span className="text-ink-muted">Stamp Duty (varies by state)</span>
            <span className="font-semibold text-ink">₹2,000 – ₹10,000</span>
          </div>
          <div className="flex justify-between text-[13px] pt-2 border-t border-border">
            <span className="font-semibold text-ink">Estimated Total</span>
            <span className="font-bold text-ai">₹{(govtFee + 5000).toLocaleString()} – ₹{(govtFee + 13000).toLocaleString()}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function StepObjects({ data, onChange, aiLoading, onAiGenerate }: { data: FormData; onChange: (k: keyof FormData, v: any) => void; aiLoading: boolean; onAiGenerate: () => void }) {
  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-[13px] font-semibold text-ink">Objects Clause</p>
          <p className="text-[12px] text-ink-muted">Defines the purpose and activities of your company</p>
        </div>
        <button
          onClick={onAiGenerate}
          disabled={aiLoading}
          className="flex items-center gap-2 bg-ai-light text-ai text-[12px] font-semibold px-4 py-2 rounded-lg hover:bg-ai hover:text-white transition-all disabled:opacity-50"
        >
          {aiLoading ? (
            <span className="w-3.5 h-3.5 border-2 border-ai/30 border-t-ai rounded-full animate-spin" />
          ) : '✨'}
          AI Draft
        </button>
      </div>
      <div>
        <label className="block text-[12px] font-semibold text-ink-muted uppercase tracking-wide mb-2">Main Objects</label>
        <textarea
          value={data.mainObjects}
          onChange={e => onChange('mainObjects', e.target.value)}
          rows={5}
          className="w-full bg-surface-2 border border-border rounded-xl px-4 py-3 text-[13px] text-ink focus:outline-none focus:ring-2 focus:ring-ai/20 focus:border-ai/40 resize-none leading-relaxed"
        />
      </div>
      <div>
        <label className="block text-[12px] font-semibold text-ink-muted uppercase tracking-wide mb-2">Other Objects (Optional)</label>
        <textarea
          value={data.otherObjects}
          onChange={e => onChange('otherObjects', e.target.value)}
          rows={3}
          className="w-full bg-surface-2 border border-border rounded-xl px-4 py-3 text-[13px] text-ink focus:outline-none focus:ring-2 focus:ring-ai/20 focus:border-ai/40 resize-none leading-relaxed"
        />
      </div>
      <p className="text-[11px] text-ink-subtle italic">AI-drafted objects require review by a Company Secretary before filing.</p>
    </div>
  );
}

function StepMOA({ data, onChange }: { data: FormData; onChange: (k: keyof FormData, v: any) => void }) {
  const clauses = [
    { key: 'moaClause1', label: 'Clause I — Name', value: data.moaClause1 },
    { key: 'moaClause2', label: 'Clause II — Registered Office', value: data.moaClause2 },
  ];
  return (
    <div className="space-y-5">
      <div className="bg-ai-light border border-ai/20 rounded-xl p-4">
        <p className="text-[13px] font-semibold text-ai mb-1">eMOA (INC-33) — Electronic Memorandum of Association</p>
        <p className="text-[12px] text-ink-muted">The MOA defines the company's constitution and relationship with the outside world. SPICe+ uses the electronic form INC-33.</p>
      </div>
      {clauses.map(c => (
        <div key={c.key}>
          <label className="block text-[12px] font-semibold text-ink-muted uppercase tracking-wide mb-2">{c.label}</label>
          <textarea
            value={c.value}
            onChange={e => onChange(c.key as keyof FormData, e.target.value)}
            rows={3}
            className="w-full bg-surface-2 border border-border rounded-xl px-4 py-3 text-[13px] text-ink focus:outline-none focus:ring-2 focus:ring-ai/20 focus:border-ai/40 resize-none"
          />
        </div>
      ))}
      <div className="bg-surface-2 rounded-xl p-4">
        <p className="text-[12px] font-semibold text-ink-muted mb-2">Clause III — Objects (from Step 6)</p>
        <p className="text-[12px] text-ink leading-relaxed line-clamp-3">{data.mainObjects}</p>
      </div>
      <div className="bg-surface-2 rounded-xl p-4">
        <p className="text-[12px] font-semibold text-ink-muted mb-2">Clause IV — Liability</p>
        <p className="text-[12px] text-ink">The liability of the members is limited.</p>
      </div>
      <div className="bg-surface-2 rounded-xl p-4">
        <p className="text-[12px] font-semibold text-ink-muted mb-2">Clause V — Capital</p>
        <p className="text-[12px] text-ink">The share capital of the company is ₹{parseInt(data.authorisedCapital || '0').toLocaleString()} divided into {Math.floor(parseInt(data.authorisedCapital || '0') / 10)} equity shares of ₹10 each.</p>
      </div>
    </div>
  );
}

function StepAOA({ data, onChange }: { data: FormData; onChange: (k: keyof FormData, v: any) => void }) {
  return (
    <div className="space-y-5">
      <div className="bg-ai-light border border-ai/20 rounded-xl p-4">
        <p className="text-[13px] font-semibold text-ai mb-1">eAOA (INC-34) — Electronic Articles of Association</p>
        <p className="text-[12px] text-ink-muted">The AOA governs the internal management of the company. For Private Limited Companies, Table F is the standard template.</p>
      </div>
      <div>
        <label className="block text-[12px] font-semibold text-ink-muted uppercase tracking-wide mb-2">AOA Type</label>
        <div className="space-y-3">
          {[
            { value: 'Table F (Standard)', desc: 'Standard articles for Private Limited Companies. Recommended for most startups.' },
            { value: 'Custom AOA', desc: 'Customised articles with specific provisions. Requires CS review.' },
          ].map(opt => (
            <label key={opt.value} className={`flex items-start gap-3 p-4 rounded-xl border cursor-pointer transition-all ${data.aoaType === opt.value ? 'border-ai/30 bg-ai-light' : 'border-border hover:border-ai/20'}`}>
              <input type="radio" name="aoaType" value={opt.value} checked={data.aoaType === opt.value} onChange={e => onChange('aoaType', e.target.value)} className="mt-0.5" />
              <div>
                <p className="text-[13px] font-semibold text-ink">{opt.value}</p>
                <p className="text-[12px] text-ink-muted mt-0.5">{opt.desc}</p>
              </div>
            </label>
          ))}
        </div>
      </div>
      <div className="bg-surface-2 rounded-xl p-4">
        <p className="text-[12px] font-semibold text-ink-muted mb-3">Key Provisions (Table F)</p>
        <div className="space-y-2">
          {[
            'Share transfer restrictions — prior board approval required',
            'Quorum for board meetings — minimum 2 directors',
            'Annual General Meeting — within 6 months of financial year end',
            'Dividend distribution — as per Companies Act 2013',
          ].map(p => (
            <div key={p} className="flex items-start gap-2">
              <span className="text-success text-[11px] mt-0.5">✓</span>
              <p className="text-[12px] text-ink-muted">{p}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function StepINC9({ data, onChange }: { data: FormData; onChange: (k: keyof FormData, v: any) => void }) {
  const declarations = [
    'I am not disqualified from being appointed as a Director under Section 164 of the Companies Act, 2013.',
    'I have not been convicted of any offence in connection with the promotion, formation or management of any company.',
    'I have not been found guilty of any fraud or misfeasance or breach of duty to any company under this Act.',
    'I have not been convicted of any offence under Section 188 of the Companies Act, 2013.',
    'I hereby declare that all the information given in this form and its attachments is true, correct and complete.',
  ];

  const toggleDeclaration = (i: number) => {
    const updated = [...data.inc9Declarations];
    updated[i] = !updated[i];
    onChange('inc9Declarations', updated);
  };

  return (
    <div className="space-y-5">
      <div className="bg-ai-light border border-ai/20 rounded-xl p-4">
        <p className="text-[13px] font-semibold text-ai mb-1">INC-9 — Declaration by Subscribers & First Directors</p>
        <p className="text-[12px] text-ink-muted">Each subscriber and first director must declare that they are not disqualified under the Companies Act, 2013.</p>
      </div>
      <div className="space-y-3">
        {declarations.map((decl, i) => (
          <label key={i} className={`flex items-start gap-3 p-4 rounded-xl border cursor-pointer transition-all ${data.inc9Declarations[i] ? 'border-success/30 bg-success-light/30' : 'border-border hover:border-ai/20'}`}>
            <input
              type="checkbox"
              checked={data.inc9Declarations[i] ?? false}
              onChange={() => toggleDeclaration(i)}
              className="mt-0.5 rounded"
            />
            <p className="text-[13px] text-ink leading-relaxed">{decl}</p>
          </label>
        ))}
      </div>
      <div className="bg-warning/8 border border-warning/20 rounded-xl p-4">
        <p className="text-[12px] font-semibold text-warning mb-1">Legal Declaration</p>
        <p className="text-[12px] text-ink-muted">By checking all boxes, each subscriber and first director confirms the above declarations are true. False declarations are punishable under Section 448 of the Companies Act, 2013.</p>
      </div>
    </div>
  );
}

function StepDocuments({ data, onChange }: { data: FormData; onChange: (k: keyof FormData, v: any) => void }) {
  const required = [
    { name: 'PAN Card - All Directors', uploaded: data.documentsUploaded.some(d => d.includes('PAN Card')) },
    { name: 'Aadhaar / Passport - All Directors', uploaded: data.documentsUploaded.some(d => d.includes('Aadhaar')) },
    { name: 'Address Proof - Registered Office', uploaded: false },
    { name: 'NOC from Property Owner', uploaded: false },
    { name: 'Utility Bill (not older than 2 months)', uploaded: false },
    { name: 'Passport-size Photos - All Directors', uploaded: false },
  ];

  return (
    <div className="space-y-5">
      <div className="space-y-3">
        {required.map((doc, i) => (
          <div key={i} className={`flex items-center justify-between p-4 rounded-xl border ${doc.uploaded ? 'border-success/30 bg-success-light/20' : 'border-border bg-white'}`}>
            <div className="flex items-center gap-3">
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${doc.uploaded ? 'bg-success-light' : 'bg-surface-3'}`}>
                <span className="text-[14px]">{doc.uploaded ? '✓' : '📄'}</span>
              </div>
              <div>
                <p className="text-[13px] font-medium text-ink">{doc.name}</p>
                <p className="text-[11px] text-ink-muted">{doc.uploaded ? 'Uploaded' : 'Required'}</p>
              </div>
            </div>
            {!doc.uploaded && (
              <button
                onClick={() => onChange('documentsUploaded', [...data.documentsUploaded, doc.name])}
                className="text-[12px] font-semibold text-ai bg-ai-light px-3 py-1.5 rounded-lg hover:bg-ai hover:text-white transition-all"
              >
                Upload
              </button>
            )}
          </div>
        ))}
      </div>
      <div className="bg-surface-2 rounded-xl p-4">
        <p className="text-[12px] font-semibold text-ink-muted mb-2">DigiLocker Integration</p>
        <p className="text-[12px] text-ink-muted mb-3">Connect DigiLocker to auto-fetch Aadhaar, PAN, and other government documents.</p>
        <button className="text-[12px] font-semibold text-ai bg-ai-light px-4 py-2 rounded-lg hover:bg-ai hover:text-white transition-all">
          Connect DigiLocker →
        </button>
      </div>
    </div>
  );
}

function StepReview({ data, onChange }: { data: FormData; onChange: (k: keyof FormData, v: any) => void }) {
  const checks = [
    { label: 'Company name availability', score: 85, status: 'warning', note: 'Name check pending with MCA RUN' },
    { label: 'Director details complete', score: 92, status: 'success', note: 'All required fields filled' },
    { label: 'Capital structure valid', score: 100, status: 'success', note: 'Paid-up ≤ Authorised capital' },
    { label: 'Objects clause quality', score: 88, status: 'success', note: 'AI-drafted, review recommended' },
    { label: 'Documents uploaded', score: 50, status: 'warning', note: '3 of 6 documents uploaded' },
    { label: 'INC-9 declarations', score: 100, status: 'success', note: 'All declarations confirmed' },
  ];
  const avgScore = Math.round(checks.reduce((s, c) => s + c.score, 0) / checks.length);

  return (
    <div className="space-y-5">
      <div className="bg-white rounded-xl border border-border p-5 flex items-center gap-5">
        <div className="relative w-20 h-20 flex-shrink-0">
          <svg className="w-20 h-20 -rotate-90" viewBox="0 0 80 80">
            <circle cx="40" cy="40" r="32" fill="none" stroke="#f1f5f9" strokeWidth="8" />
            <circle cx="40" cy="40" r="32" fill="none" stroke={avgScore >= 80 ? '#22c55e' : '#f59e0b'} strokeWidth="8"
              strokeDasharray={`${(avgScore / 100) * 201} 201`} strokeLinecap="round" />
          </svg>
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="text-xl font-bold text-ink">{avgScore}</span>
          </div>
        </div>
        <div>
          <p className="text-[15px] font-bold text-ink">AI Readiness Score</p>
          <p className="text-[13px] text-ink-muted mt-1">
            {avgScore >= 80 ? 'Your application is ready for filing. A few items need attention.' : 'Complete the pending items before filing.'}
          </p>
        </div>
      </div>
      <div className="space-y-2">
        {checks.map((c, i) => (
          <div key={i} className="flex items-center justify-between p-3.5 bg-white rounded-xl border border-border">
            <div className="flex items-center gap-3">
              <span className={`w-2 h-2 rounded-full ${c.status === 'success' ? 'bg-success' : 'bg-warning'}`} />
              <div>
                <p className="text-[13px] font-medium text-ink">{c.label}</p>
                <p className="text-[11px] text-ink-muted">{c.note}</p>
              </div>
            </div>
            <span className={`text-[12px] font-bold ${c.status === 'success' ? 'text-success' : 'text-warning'}`}>{c.score}%</span>
          </div>
        ))}
      </div>
      <label className="flex items-start gap-3 p-4 bg-white rounded-xl border border-border cursor-pointer">
        <input type="checkbox" checked={data.reviewConfirmed} onChange={e => onChange('reviewConfirmed', e.target.checked)} className="mt-0.5 rounded" />
        <p className="text-[13px] text-ink leading-relaxed">
          I confirm that all information provided is accurate and complete. I understand that filing false information is an offence under the Companies Act, 2013.
        </p>
      </label>
    </div>
  );
}

function StepSubmit({ data }: { data: FormData }) {
  return (
    <div className="space-y-5">
      <div className="bg-ai-light border border-ai/20 rounded-xl p-6 text-center">
        <div className="w-16 h-16 bg-ai rounded-2xl flex items-center justify-center mx-auto mb-4">
          <span className="text-3xl">🚀</span>
        </div>
        <h3 className="text-[17px] font-bold text-ink mb-2">Ready to File with MCA</h3>
        <p className="text-[13px] text-ink-muted">Your SPICe+ form package is prepared. This includes INC-32 (SPICe+), INC-33 (eMOA), INC-34 (eAOA), and INC-9.</p>
      </div>
      <div className="bg-white rounded-xl border border-border p-5">
        <p className="text-[13px] font-semibold text-ink mb-4">Filing Package Summary</p>
        <div className="space-y-2.5">
          {[
            { form: 'SPICe+ (INC-32)', desc: 'Main incorporation form', status: '✓ Ready' },
            { form: 'eMOA (INC-33)', desc: 'Memorandum of Association', status: '✓ Ready' },
            { form: 'eAOA (INC-34)', desc: 'Articles of Association', status: '✓ Ready' },
            { form: 'INC-9', desc: 'Subscriber declarations', status: '✓ Ready' },
            { form: 'AGILE-PRO-S', desc: 'GST, ESIC, EPFO, Bank account', status: '⚠ Optional' },
          ].map(f => (
            <div key={f.form} className="flex items-center justify-between py-2 border-b border-border last:border-0">
              <div>
                <p className="text-[13px] font-semibold text-ink">{f.form}</p>
                <p className="text-[11px] text-ink-muted">{f.desc}</p>
              </div>
              <span className={`text-[12px] font-semibold ${f.status.startsWith('✓') ? 'text-success' : 'text-warning'}`}>{f.status}</span>
            </div>
          ))}
        </div>
      </div>
      <div className="bg-surface-2 rounded-xl p-4">
        <p className="text-[12px] font-semibold text-ink-muted mb-2">What happens next?</p>
        <div className="space-y-2">
          {[
            'MCA processes the application (2–7 business days)',
            'Certificate of Incorporation (COI) issued digitally',
            'Company PAN and TAN auto-generated',
            'DIN allotted to all directors',
          ].map((step, i) => (
            <div key={i} className="flex items-start gap-2">
              <span className="text-[11px] font-bold text-ai mt-0.5">{i + 1}.</span>
              <p className="text-[12px] text-ink-muted">{step}</p>
            </div>
          ))}
        </div>
      </div>
      {!data.reviewConfirmed && (
        <div className="bg-warning/8 border border-warning/20 rounded-xl p-3">
          <p className="text-[12px] font-semibold text-warning">⚠ Please confirm the legal declaration in Step 11 before submitting.</p>
        </div>
      )}
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function CompanyRegistrationPage() {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState<FormData>(defaultFormData);
  const [aiLoading, setAiLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const { success, error: toastError, info } = useToast();

  const onChange = (key: keyof FormData, value: any) => {
    setFormData(prev => ({ ...prev, [key]: value }));
  };

  const handleAiDraftObjects = async () => {
    setAiLoading(true);
    try {
      const res = await fetch('/api/ai', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          type: 'chat',
          messages: [{
            role: 'user',
            content: `Draft a professional "Main Objects" clause for a ${formData.businessType} named "${formData.proposedName1}" that deals in: ${formData.mainObjects}. Format it as a formal legal clause suitable for MCA filing.`,
          }],
        }),
      });
      const data = await res.json();
      if (data.response) {
        onChange('mainObjects', data.response);
        success('Objects clause drafted', 'AI has generated a professional objects clause.');
      }
    } catch {
      toastError('AI unavailable', 'Using existing objects clause. Add OPENROUTER_API_KEY to enable AI drafting.');
    } finally {
      setAiLoading(false);
    }
  };

  const handleNext = () => {
    if (currentStep < 12) {
      setCurrentStep(prev => prev + 1);
      info(`Step ${currentStep + 1} of 12`, STEPS[currentStep].label);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) setCurrentStep(prev => prev - 1);
  };

  const handleSubmit = async () => {
    if (!formData.reviewConfirmed) {
      toastError('Declaration required', 'Please confirm the legal declaration in Step 11.');
      setCurrentStep(11);
      return;
    }
    setSubmitting(true);
    await new Promise(r => setTimeout(r, 2500));
    setSubmitting(false);
    setSubmitted(true);
    success('Application submitted!', 'Your SPICe+ filing has been submitted to MCA.');
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-surface-2 font-sans">
        <AppNav variant="app" activePath="/register" />
        <main className="sm:ml-60 pt-14 sm:pt-0 min-h-screen flex items-center justify-center p-6">
          <div className="bg-white rounded-2xl border border-border p-10 text-center max-w-md w-full">
            <div className="w-20 h-20 bg-success-light rounded-full flex items-center justify-center mx-auto mb-5">
              <span className="text-4xl">🎉</span>
            </div>
            <h2 className="text-2xl font-bold text-ink mb-2">Application Submitted!</h2>
            <p className="text-[14px] text-ink-muted mb-2">Your SPICe+ filing has been submitted to MCA.</p>
            <p className="text-[13px] text-ink-muted mb-6">Reference: <span className="font-semibold text-ink">MCA-2026-{Math.floor(Math.random() * 900000 + 100000)}</span></p>
            <div className="bg-surface-2 rounded-xl p-4 mb-6 text-left">
              <p className="text-[12px] font-semibold text-ink-muted mb-2">What happens next?</p>
              <div className="space-y-1.5">
                {['MCA processes in 2–7 business days', 'Certificate of Incorporation issued digitally', 'Company PAN & TAN auto-generated'].map(s => (
                  <div key={s} className="flex items-center gap-2">
                    <span className="text-success text-[11px]">✓</span>
                    <p className="text-[12px] text-ink-muted">{s}</p>
                  </div>
                ))}
              </div>
            </div>
            <Link href="/dashboard" className="block w-full bg-ai text-white font-semibold py-3 rounded-xl hover:bg-ai-dark transition-colors text-[14px]">
              Go to Dashboard →
            </Link>
          </div>
        </main>
      </div>
    );
  }

  const renderStep = () => {
    switch (currentStep) {
      case 1: return <StepBusinessPassport data={formData} onChange={onChange} />;
      case 2: return <StepDirectors data={formData} onChange={onChange} />;
      case 3: return <StepShareholders data={formData} onChange={onChange} />;
      case 4: return <StepRegisteredOffice data={formData} onChange={onChange} />;
      case 5: return <StepCapital data={formData} onChange={onChange} />;
      case 6: return <StepObjects data={formData} onChange={onChange} aiLoading={aiLoading} onAiGenerate={handleAiDraftObjects} />;
      case 7: return <StepMOA data={formData} onChange={onChange} />;
      case 8: return <StepAOA data={formData} onChange={onChange} />;
      case 9: return <StepINC9 data={formData} onChange={onChange} />;
      case 10: return <StepDocuments data={formData} onChange={onChange} />;
      case 11: return <StepReview data={formData} onChange={onChange} />;
      case 12: return <StepSubmit data={formData} />;
      default: return null;
    }
  };

  const progress = ((currentStep - 1) / 11) * 100;

  return (
    <PageErrorBoundary>
      <div className="min-h-screen bg-surface-2 font-sans">
        <AppNav variant="app" activePath="/register" />

        <main className="sm:ml-60 pt-14 sm:pt-0 min-h-screen">
          <div className="max-w-5xl mx-auto px-4 md:px-6 py-6 md:py-8">

            {/* Header */}
            <div className="mb-6">
              <div className="flex items-center gap-2 mb-1">
                <Link href="/dashboard" className="text-[12px] text-ink-muted hover:text-ink transition-colors">Dashboard</Link>
                <span className="text-ink-subtle">›</span>
                <span className="text-[12px] text-ink font-medium">Company Registration</span>
              </div>
              <h1 className="text-2xl font-bold text-ink">Company Registration</h1>
              <p className="text-[14px] text-ink-muted mt-1">SPICe+ (INC-32) with eMOA, eAOA & INC-9 — AI-assisted filing</p>
            </div>

            {/* Progress Bar */}
            <div className="mb-6">
              <div className="flex items-center justify-between mb-2">
                <span className="text-[12px] font-semibold text-ink-muted">Step {currentStep} of 12 — {STEPS[currentStep - 1].label}</span>
                <span className="text-[12px] font-semibold text-ai">{Math.round(progress)}% complete</span>
              </div>
              <div className="w-full bg-surface-3 rounded-full h-2">
                <div className="bg-ai h-2 rounded-full transition-all duration-500" style={{ width: `${progress}%` }} />
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
              {/* Step Sidebar */}
              <div className="hidden lg:block lg:col-span-1">
                <div className="bg-white rounded-xl border border-border overflow-hidden sticky top-6">
                  {STEPS.map(step => (
                    <button
                      key={step.id}
                      onClick={() => setCurrentStep(step.id)}
                      className={`w-full flex items-center gap-3 px-4 py-3 text-left border-b border-border last:border-0 transition-all ${
                        currentStep === step.id
                          ? 'bg-ai-light'
                          : step.id < currentStep
                          ? 'hover:bg-surface-2' :'opacity-50 cursor-default'
                      }`}
                      disabled={step.id > currentStep}
                    >
                      <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold flex-shrink-0 ${
                        step.id < currentStep ? 'bg-success text-white' :
                        step.id === currentStep ? 'bg-ai text-white': 'bg-surface-3 text-ink-muted'
                      }`}>
                        {step.id < currentStep ? '✓' : step.id}
                      </div>
                      <div className="min-w-0">
                        <p className={`text-[11px] font-semibold truncate ${currentStep === step.id ? 'text-ai' : 'text-ink'}`}>{step.label}</p>
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Form Content */}
              <div className="lg:col-span-3">
                <div className="bg-white rounded-2xl border border-border p-6 mb-4">
                  <div className="flex items-center gap-3 mb-5 pb-4 border-b border-border">
                    <span className="text-2xl">{STEPS[currentStep - 1].icon}</span>
                    <div>
                      <h2 className="text-[16px] font-bold text-ink">{STEPS[currentStep - 1].label}</h2>
                      <p className="text-[12px] text-ink-muted">{STEPS[currentStep - 1].desc}</p>
                    </div>
                  </div>
                  {renderStep()}
                </div>

                {/* Navigation */}
                <div className="flex items-center justify-between">
                  <button
                    onClick={handleBack}
                    disabled={currentStep === 1}
                    className="flex items-center gap-2 px-5 py-2.5 border border-border rounded-xl text-[13px] font-medium text-ink-muted hover:text-ink hover:border-ai/30 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    ← Back
                  </button>
                  <div className="flex items-center gap-2">
                    {/* Mobile step indicator */}
                    <span className="lg:hidden text-[12px] text-ink-muted">{currentStep}/12</span>
                  </div>
                  {currentStep < 12 ? (
                    <button
                      onClick={handleNext}
                      className="flex items-center gap-2 bg-ai text-white font-semibold px-6 py-2.5 rounded-xl hover:bg-ai-dark transition-all text-[13px]"
                    >
                      Continue →
                    </button>
                  ) : (
                    <button
                      onClick={handleSubmit}
                      disabled={submitting || !formData.reviewConfirmed}
                      className="flex items-center gap-2 bg-success text-white font-semibold px-6 py-2.5 rounded-xl hover:bg-success/90 transition-all text-[13px] disabled:opacity-50"
                    >
                      {submitting ? (
                        <>
                          <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                          Submitting to MCA...
                        </>
                      ) : '🚀 Submit to MCA'}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    </PageErrorBoundary>
  );
}
